// /api/auth/sessions を使ったセッション管理パネル

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Monitor, Smartphone, Trash2, Power, ShieldCheck, RefreshCw } from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { authApi, type SessionItem } from "../../api/auth";
import { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";

function deviceIcon(item: SessionItem) {
  const os = (item.os_family || "").toLowerCase();
  if (os.includes("ios") || os.includes("android")) {
    return <Smartphone size={18} />;
  }
  return <Monitor size={18} />;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export const SessionsPanel = () => {
  const [items, setItems] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { sessionId, logout, logoutAll } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authApi.sessions();
      setItems(resp.items);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "セッション一覧を取得できません";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRevoke(sid: string) {
    if (!window.confirm("このセッションをログアウトします。よろしいですか?")) return;
    setBusyId(sid);
    try {
      await authApi.revokeSession(sid);
      // 自分の session を消したら /login へ
      // 注意: revokeSession は DB 側を無効化するが cookies はクリアしないので、
      // 単一デバイスからのログアウトを完了させるために logout() を呼ぶ。
      // 以前は logoutAll() を呼んでいたが、これだと他デバイスのセッションも
      // 巻き添えで revoke されてしまうため、ヘッダーの「このデバイスからログアウト」と
      // 同じ logout() に統一する。
      if (sid === sessionId) {
        await logout();
        navigate("/login", { replace: true });
        return;
      }
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "削除に失敗しました";
      setError(msg);
    } finally {
      setBusyId(null);
    }
  }

  async function handleLogoutAll() {
    if (!window.confirm("すべてのデバイスからログアウトします。よろしいですか?")) return;
    await logoutAll();
    navigate("/login", { replace: true });
  }

  return (
    <Card>
      <div className="settings-card-head">
        <div className="settings-card-title-row">
          <div className="settings-card-icon"><ShieldCheck size={20} /></div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>セッション管理</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              現在ログイン中の全デバイス一覧。信頼済みデバイスは検証コード不要。
            </div>
          </div>
        </div>
        <Button
          variant="secondary"
          iconLeft={<RefreshCw size={13} />}
          onClick={load}
          disabled={loading}
        >
          再読み込み
        </Button>
      </div>

      {error && (
        <div className="login-error" style={{ marginBottom: 12 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>読み込み中...</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
          アクティブなセッションはありません
        </div>
      ) : (
        <div className="sessions-list">
          {items.map((item) => (
            <div key={item.id} className="sessions-item">
              <div className="sessions-item-icon">{deviceIcon(item)}</div>
              <div className="sessions-item-info">
                <div className="sessions-item-title">
                  {item.device_name || "Unknown device"}
                  {item.current && (
                    <Badge color="blue" dot>このデバイス</Badge>
                  )}
                  {item.trusted && (
                    <Badge color="green" dot>信頼済み</Badge>
                  )}
                </div>
                <div className="sessions-item-meta">
                  {[item.browser_family, item.os_family].filter(Boolean).join(" / ") || "Unknown browser"}
                  {" · "}
                  {item.ip || "—"}
                  {item.location ? ` · ${item.location}` : ""}
                </div>
                <div className="sessions-item-meta">
                  最終アクセス: {formatTime(item.last_seen_at)}
                </div>
              </div>
              <Button
                variant="ghost"
                iconLeft={<Trash2 size={13} />}
                onClick={() => handleRevoke(item.id)}
                disabled={busyId === item.id}
              >
                {busyId === item.id ? "削除中..." : "ログアウト"}
              </Button>
            </div>
          ))}
        </div>
      )}

      <hr className="divider" />

      <div className="row gap-8">
        <Button variant="secondary" iconLeft={<Power size={13} />} onClick={handleLogoutAll}>
          すべてのデバイスからログアウト
        </Button>
      </div>
    </Card>
  );
};